import { type ComponentProps, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import isEqual from 'lodash-es/isEqual';
import { XIcon } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';

import { simulationsEndpoints } from '@/api/endpoints';
import { AuthoringValidationBanner } from '@/components/atoms/AuthoringValidationBanner/AuthoringValidationBanner';
import { JsonTextarea } from '@/components/atoms/JsonTextarea/JsonTextarea';
import {
  SidebarWrapper,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { UnsavedChangesModal } from '@/components/organisms/Modals/UnsavedChangesModal/UnsavedChangesModal';
import { useResponsiveSidebarClose } from '@/components/organisms/Sidebar/ResponsiveSidebarPanel';
import { Button } from '@/components/ui/button';
import { PopoverCombobox } from '@/components/ui/custom/PopoverCombobox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useDraftValidation } from '@/hooks/useDraftValidation';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { cn } from '@/lib/utils';
import { useSimulationDraft } from '@/modules/simulations/hooks/useSimulationDraft';
import useSimulations from '@/modules/simulations/hooks/useSimulations';
import {
  parseStartingContext,
  stringifyStartingContext,
} from '@/modules/simulations/startingContext';
import type { Simulation, SimulationChannelType } from '@/modules/simulations/types';
import {
  CADENCE_LABEL_KEYS,
  CHANNEL_LABEL_KEYS,
  SCHEDULE_CADENCES,
  SIMULATION_CHANNEL_TYPES,
} from '@/modules/simulations/types';
import { uniqueSimulationName } from '@/modules/simulations/utils';
import { createSimulationSchema, type SimulationFormValues } from '@/modules/simulations/validator';
import { selectBrainId } from '@/redux/session/selectors';
import { setSidebarMeta } from '@/redux/sidebar/actions';
import { selectSidebarMeta } from '@/redux/sidebar/selectors';
import { trackEvent } from '@/segment/segment';
import type { ValidateSimulationBody } from '@/shared/types/authoring-validation';
import { EventName } from '@/shared/types/segment';
import type { SimulationDraft } from '@/shared/types/simulation-draft';
import { useHasDirtyFields } from '@/util/util';

const Field = ({ className, ...props }: ComponentProps<typeof FormItem>) => (
  <FormItem className={cn('flex flex-col gap-2 space-y-0', className)} {...props} />
);

const FieldLabel = ({ className, ...props }: ComponentProps<typeof FormLabel>) => (
  <FormLabel className={cn('mb-0', className)} {...props} />
);

const FieldFooter = ({ children }: { children?: ReactNode }) => (
  <div className="flex flex-col gap-0.5">
    {children != null && <FormDescription>{children}</FormDescription>}
    <FormMessage />
  </div>
);

const SkeletonField = ({ control }: { control: string }) => (
  <div className="flex flex-col gap-2">
    <Skeleton className="h-4 w-32" />
    <Skeleton className={control} />
    <Skeleton className="h-4 w-56" />
  </div>
);

const DraftingSkeleton = () => (
  <div className="flex flex-col gap-6" aria-hidden>
    <SkeletonField control="h-9 w-full" />
    <SkeletonField control="h-25 w-full" />
    <SkeletonField control="h-25 w-full" />
    <SkeletonField control="h-32 w-full" />
    <div className="flex flex-col gap-2">
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-4 w-56" />
      <div className="flex flex-col gap-2">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-5 w-24" />
      </div>
    </div>
    <SkeletonField control="h-9 w-full" />
    <SkeletonField control="h-9 w-full" />
  </div>
);

const DRAFT_FAILED_TOAST_ID = 'simulation-draft-failed';

const EMPTY_FORM_VALUES: SimulationFormValues = {
  name: '',
  scenario: '',
  pass_criteria: '',
  channel_type: 'chat',
  starting_context: '',
  category: null,
  max_turns: 20,
  schedule_frequency: null,
};

const toFormValues = (simulation: Simulation): SimulationFormValues => ({
  name: simulation.name,
  scenario: simulation.scenario,
  pass_criteria: simulation.pass_criteria,
  channel_type: simulation.channel_type,
  starting_context: stringifyStartingContext(simulation.starting_context),
  category: simulation.category,
  max_turns: simulation.max_turns,
  schedule_frequency: simulation.schedule_frequency,
});

const comparableValues = (values: SimulationFormValues) => ({
  ...values,
  category: values.category ?? null,
  schedule_frequency: values.schedule_frequency ?? null,
  starting_context: parseStartingContext(values.starting_context).value ?? values.starting_context,
});

interface CreateSimulationPanelProps {
  mode?: 'create' | 'edit';
}

export const CreateSimulationPanel = ({ mode = 'create' }: CreateSimulationPanelProps) => {
  const { t } = useTranslation();
  const onClose = useResponsiveSidebarClose();
  const dispatch = useDispatch();
  const brainId = useSelector(selectBrainId);
  const meta = useSelector(selectSidebarMeta);
  const {
    simulations,
    isSimulationsLoading,
    categories,
    createSimulation,
    updateSimulation,
    triggerRun,
    createStatus,
    updateStatus,
  } = useSimulations(brainId);

  // Captured on button click so handleSubmit knows whether to chain a run after create.
  const intentRef = useRef<'create' | 'createAndRun'>('create');

  const isEdit = mode === 'edit';
  // Track the simulation the panel is actually showing. When the sidebar meta changes (e.g. the
  // user clicks a different row while editing), we only adopt the new id after a clean-form
  // check — otherwise we stash it and prompt via the unsaved-changes modal.
  const [simulationId, setSimulationId] = useState(meta?.simulationId);
  const pendingSwitchIdRef = useRef<string | undefined>(undefined);
  const simulation = isEdit
    ? simulations?.find((s) => s.simulation_id === simulationId)
    : undefined;

  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);

  const namedCategories = useMemo(
    () => categories.filter((c): c is string => c !== null),
    [categories]
  );

  // Rebuild the schema whenever language, brain, or the edited sim changes — the latter so
  // edit mode skips the unique-name check when the user hasn't touched the name.
  const schema = useMemo(
    () => createSimulationSchema(t, brainId ?? '', simulation?.name),
    [t, brainId, simulation?.name]
  );

  const requestedContext = meta?.startingContext;
  const seededContextRef = useRef(requestedContext ?? '');
  const seededDraftRef = useRef<SimulationDraft | null>(null);
  const createDefaults = (): SimulationFormValues => ({
    ...EMPTY_FORM_VALUES,
    ...(seededDraftRef.current ?? {}),
    starting_context: seededContextRef.current,
  });

  const form = useForm<SimulationFormValues>({
    resolver: yupResolver<SimulationFormValues, unknown, SimulationFormValues>(schema),
    defaultValues: createDefaults(),
  });

  useEffect(() => {
    if (simulation) {
      form.reset(toFormValues(simulation));
    }
  }, [simulation, form]);

  useEffect(() => {
    if (requestedContext === undefined || requestedContext === seededContextRef.current) {
      return;
    }
    seededContextRef.current = requestedContext;
    form.setValue('starting_context', requestedContext, { shouldValidate: true });
  }, [requestedContext, form]);

  useEffect(() => {
    if (seededContextRef.current) {
      form.trigger('starting_context');
    }
  }, [form]);

  const carriedTranscript = isEdit ? '' : (meta?.transcript ?? '');
  const { draft, isDrafting, hasFailed } = useSimulationDraft({
    brainId,
    transcript: carriedTranscript,
    startingContext: requestedContext ?? '',
  });

  const isDraftPending = isDrafting || (Boolean(carriedTranscript) && isSimulationsLoading);

  useEffect(() => {
    if (hasFailed) {
      toast.warning(t('simulations.drafting_failed_title'), {
        id: DRAFT_FAILED_TOAST_ID,
        description: t('simulations.drafting_failed_description'),
      });
    }
  }, [hasFailed, t]);

  useEffect(() => {
    if (!draft || isSimulationsLoading || seededDraftRef.current) {
      return;
    }
    const seeded: SimulationDraft = {
      ...draft,
      name: uniqueSimulationName(draft.name, simulations ?? []),
    };
    seededDraftRef.current = seeded;
    // reset, not setValue: it makes the draft the baseline for the unsaved-changes check.
    form.reset({ ...form.getValues(), ...seeded });
  }, [draft, simulations, isSimulationsLoading, form]);

  // Compare the current form values against the loaded simulation (or the create-mode
  // defaults) to decide whether the user has pending changes. react-hook-form's
  // dirtyFields can report false positives after form.reset, so prefer an explicit
  // value-level check.
  const isFormModified = () =>
    !isEqual(
      comparableValues(form.getValues()),
      comparableValues(isEdit && simulation ? toFormValues(simulation) : createDefaults())
    );

  useEffect(() => {
    const nextId = meta?.simulationId;
    if (nextId === simulationId) {
      return;
    }
    if (!isEdit || !isFormModified()) {
      setSimulationId(nextId);
      return;
    }
    pendingSwitchIdRef.current = nextId;
    setIsUnsavedChangesModalOpen(true);
    // isFormModified is a stable closure over form + simulation; eslint can't see that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta?.simulationId, simulationId, isEdit]);

  const handleClose = () => {
    if (isFormModified()) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      onClose();
    }
  };

  // "Dismiss without choosing" path — Go back button, Escape, or overlay click. If a row-switch
  // was pending, drop it and revert Redux meta so everything stays aligned with the panel's
  // committed simulationId.
  const handleUnsavedModalDismiss = () => {
    setIsUnsavedChangesModalOpen(false);
    const pending = pendingSwitchIdRef.current;
    pendingSwitchIdRef.current = undefined;
    if (pending !== undefined) {
      dispatch(setSidebarMeta({ simulationId }));
    }
  };

  const isDirty = useHasDirtyFields(form.control);
  useTrackFormState({ isDirty, formId: 'simulation-form' });
  useEscapeKey(handleClose);

  const {
    recommendation,
    scheduleValidate,
    reset: resetValidation,
  } = useDraftValidation<ValidateSimulationBody>({
    endpoint: brainId ? simulationsEndpoints.validate(brainId) : '',
  });

  const getSubmitButtonText = (intent: 'create' | 'createAndRun' = 'create') => {
    if (isEdit) {
      return t('common.update');
    }
    if (intent === 'createAndRun') {
      return t('simulations.create_and_run');
    }
    return t('common.create');
  };

  // When a schedule is set, the simulation will run automatically on cadence — a manual
  // "Create & Run" button would invite "will this double-fire?" confusion. So hide it.
  const scheduleFrequency = useWatch({ control: form.control, name: 'schedule_frequency' });
  const showCreateAndRun = !isEdit && !scheduleFrequency;

  // Reset the advisory banner when the panel switches to a different simulation
  // (or back to create mode) so we don't show stale advice from the prior draft.
  useEffect(() => {
    resetValidation();
  }, [simulationId, resetValidation]);

  // Live, debounced validation: every form edit schedules a validate call; the hook
  // coalesces rapid edits into a single request after the idle window. Advisory only —
  // the recommendation banner never blocks the Update/Create button.
  // Gate on content rather than dirtyFields — react-hook-form's dirty tracking can stick
  // false after a paste→clear cycle, which used to make the banner trigger only once.
  useEffect(
    () =>
      form.subscribe({
        name: ['name', 'scenario', 'pass_criteria', 'channel_type', 'category', 'max_turns'],
        formState: { values: true },
        callback: ({ values: v }) => {
          const hasContent = !!(v.name || v.scenario || v.pass_criteria);
          if (!brainId || !hasContent) {
            resetValidation();
            return;
          }
          scheduleValidate({
            name: v.name || undefined,
            scenario: v.scenario || undefined,
            pass_criteria: v.pass_criteria || undefined,
            channel_type: v.channel_type as SimulationChannelType,
            category: v.category ?? undefined,
            max_turns: v.max_turns,
          });
        },
      }),
    [form, brainId, scheduleValidate, resetValidation]
  );

  const handleSubmit = (values: SimulationFormValues) => {
    const { value: startingContext } = parseStartingContext(values.starting_context);
    if (!startingContext) {
      return;
    }
    const data = {
      name: values.name,
      scenario: values.scenario,
      pass_criteria: values.pass_criteria,
      channel_type: values.channel_type as SimulationChannelType,
      starting_context: startingContext,
      category: values.category,
      max_turns: values.max_turns,
      schedule_frequency: values.schedule_frequency,
    };

    if (isEdit && simulationId) {
      // Keep the edit panel open on save: just clear dirty state by resetting to the
      // submitted values. The list refetch will then reconcile any server-side changes
      // (e.g. category normalization) via the `simulation` effect above.
      updateSimulation({ id: simulationId, body: data }, { onSuccess: () => form.reset(values) });
      return;
    }
    trackEvent(EventName.ClickCreateSimulation);
    const runAfterCreate = intentRef.current === 'createAndRun';
    createSimulation(data, {
      onSuccess: (resp) => {
        form.reset();
        onClose();
        if (runAfterCreate) {
          triggerRun({ simulation_ids: [resp.simulation_id] });
        }
      },
    });
  };

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle>
        <h2 className="text-base leading-5 font-medium">
          {isEdit ? t('simulations.edit_simulation') : t('simulations.create_simulation')}
        </h2>
        <Button variant="ghost-neutral" size="icon-sm" type="button" onClick={handleClose}>
          <XIcon size={16} className="text-muted-foreground" />
        </Button>
      </SidebarWrapperTitle>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="flex flex-1 flex-col overflow-hidden"
        >
          <div className="flex flex-col gap-6 overflow-y-auto px-6 py-4">
            {isDraftPending ? (
              <DraftingSkeleton />
            ) : (
              <>
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <Field>
                      <FieldLabel>{t('simulations.name')}</FieldLabel>
                      <FormControl>
                        <Input {...field} autoFocus />
                      </FormControl>
                      <FieldFooter>{t('simulations.name_description')}</FieldFooter>
                    </Field>
                  )}
                />

                <FormField
                  control={form.control}
                  name="scenario"
                  render={({ field }) => (
                    <Field>
                      <FieldLabel>{t('simulations.scenario')}</FieldLabel>
                      <FormControl>
                        <Textarea {...field} className="min-h-25" />
                      </FormControl>
                      <FieldFooter>{t('simulations.scenario_description')}</FieldFooter>
                    </Field>
                  )}
                />

                <FormField
                  control={form.control}
                  name="pass_criteria"
                  render={({ field }) => (
                    <Field>
                      <FieldLabel>{t('simulations.success_criteria')}</FieldLabel>
                      <FormControl>
                        <Textarea {...field} className="min-h-25" />
                      </FormControl>
                      <FieldFooter>{t('simulations.success_criteria_description')}</FieldFooter>
                    </Field>
                  )}
                />

                <FormField
                  control={form.control}
                  name="starting_context"
                  render={({ field }) => (
                    <Field className="gap-3">
                      <div className="flex flex-col gap-1">
                        <FieldLabel>{t('simulations.initial_context')}</FieldLabel>
                        <FormDescription>
                          {t('simulations.initial_context_description')}
                        </FormDescription>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <FormControl>
                          <JsonTextarea {...field} />
                        </FormControl>
                        <FormMessage />
                      </div>
                    </Field>
                  )}
                />

                <FormField
                  control={form.control}
                  name="channel_type"
                  render={({ field }) => (
                    <Field>
                      <div className="flex flex-col gap-1">
                        <FieldLabel>{t('simulations.channel_type')}</FieldLabel>
                        <FormDescription>
                          {t('simulations.channel_type_description')}
                        </FormDescription>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            value={field.value}
                            className="gap-2"
                          >
                            {SIMULATION_CHANNEL_TYPES.map((ch) => (
                              <RadioGroupItem key={ch} value={ch} id={`channel-${ch}`}>
                                {t(CHANNEL_LABEL_KEYS[ch])}
                              </RadioGroupItem>
                            ))}
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </div>
                    </Field>
                  )}
                />

                <FormField
                  control={form.control}
                  name="schedule_frequency"
                  render={({ field }) => (
                    <Field>
                      <FieldLabel>{t('simulations.schedule_cadence_label')}</FieldLabel>
                      <Select
                        key={field.value ?? 'none'}
                        onValueChange={(v) => field.onChange(v === 'none' ? null : v)}
                        value={field.value ?? 'none'}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">
                            {t('simulations.schedule_cadence_none')}
                          </SelectItem>
                          {SCHEDULE_CADENCES.map((c) => (
                            <SelectItem key={c} value={c}>
                              {t(CADENCE_LABEL_KEYS[c])}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FieldFooter>{t('simulations.schedule_cadence_description')}</FieldFooter>
                    </Field>
                  )}
                />

                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => {
                    const options = namedCategories.map((c) => ({ value: c, label: c }));
                    return (
                      <Field>
                        <FieldLabel>{t('simulations.category_optional')}</FieldLabel>
                        <FormControl>
                          <PopoverCombobox
                            options={options}
                            value={field.value ?? ''}
                            onChange={(v) => field.onChange(v || null)}
                            triggerPlaceholder={t('simulations.category_placeholder')}
                            onAddNewOption={(v) => field.onChange(v.trim() || null)}
                            createNewText={(search) =>
                              search
                                ? t('simulations.category_add_new', { 0: search })
                                : t('simulations.category_add_new_plain')
                            }
                            allowDeselect
                          />
                        </FormControl>
                        <FieldFooter>{t('simulations.category_description')}</FieldFooter>
                      </Field>
                    );
                  }}
                />

                <AuthoringValidationBanner recommendation={recommendation} />
              </>
            )}
          </div>

          {/* Create & Run renders first so implicit Enter-submit fires the primary CTA. */}
          <div className="flex shrink-0 flex-row-reverse gap-2 p-6">
            {showCreateAndRun && (
              <Button
                type="submit"
                onClick={() => {
                  intentRef.current = 'createAndRun';
                }}
                isLoading={createStatus === 'pending'}
                disabled={isDraftPending}
              >
                {getSubmitButtonText('createAndRun')}
              </Button>
            )}
            <Button
              type="submit"
              variant={showCreateAndRun ? 'outline-neutral' : 'primary'}
              onClick={() => {
                intentRef.current = 'create';
              }}
              isLoading={createStatus === 'pending' || updateStatus === 'pending'}
              disabled={isDraftPending}
            >
              {getSubmitButtonText('create')}
            </Button>
            <Button type="button" variant="ghost-neutral" onClick={handleClose}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      </Form>

      {isUnsavedChangesModalOpen && (
        <UnsavedChangesModal
          onDiscard={() => {
            setIsUnsavedChangesModalOpen(false);
            const pending = pendingSwitchIdRef.current;
            pendingSwitchIdRef.current = undefined;
            if (pending !== undefined) {
              setSimulationId(pending);
            } else {
              onClose();
            }
          }}
          onGoBack={handleUnsavedModalDismiss}
          onOpenChange={(open) => {
            if (!open) {
              handleUnsavedModalDismiss();
            }
          }}
        />
      )}
    </SidebarWrapper>
  );
};
